import { useEffect, useState } from "react";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import ShareIcon from "@mui/icons-material/Share";
import { LoadingButton } from "@mui/lab";
import ProjectService from "../../../services/ProjectService";
import { useNotification } from "../../../context/NotificationContext";
import { formatDate } from "../../../utils/formatDate";
import ShareProjectDialog from "./ShareProjectDialog";

const formatSharedUser = (accessUser) => {
    const name =
        typeof accessUser?.full_name === "string"
            ? accessUser.full_name.trim()
            : "";
    const email =
        typeof accessUser?.email === "string" ? accessUser.email.trim() : "";

    if (name && email) {
        return `${name} (${email})`;
    }

    return email || accessUser?.user || "-";
};

const ShareProjectTab = ({ projectId }) => {
    const showNotification = useNotification();
    const [accessUsers, setAccessUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
    const [removeTarget, setRemoveTarget] = useState(null);
    const [isRemoving, setIsRemoving] = useState(false);

    useEffect(() => {
        let isMounted = true;

        const getAccessUsers = async () => {
            if (!projectId) return;
            try {
                setIsLoading(true);
                const response = await ProjectService.getProjectAccess(projectId);
                if (isMounted) {
                    setAccessUsers(response.users || []);
                }
            } catch (error) {
                console.error("Failed to load project access:", error);
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        getAccessUsers();

        return () => {
            isMounted = false;
        };
    }, [projectId]);

    const handleRemoveAccess = async () => {
        if (!removeTarget) return;

        try {
            setIsRemoving(true);
            const response = await ProjectService.removeProjectAccess(
                projectId,
                removeTarget.id,
            );
            setAccessUsers(response.users || []);
            setRemoveTarget(null);
            showNotification("Access removed successfully.", "success");
        } catch (error) {
            console.error("Failed to remove project access:", error);
            showNotification("Failed to remove access.", "error");
        } finally {
            setIsRemoving(false);
        }
    };

    return (
        <Box>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 2,
                    gap: 2,
                }}
            >
                <Typography variant="h6" color="primary">
                    Shared Users
                </Typography>
                <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<ShareIcon />}
                    onClick={() => setIsShareDialogOpen(true)}
                >
                    Share
                </Button>
            </Box>
            {isLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer component={Paper} sx={{ boxShadow: "none" }}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell>User</TableCell>
                                <TableCell>Access granted date</TableCell>
                                <TableCell>Account created</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {accessUsers.length ? (
                                accessUsers.map((accessUser) => (
                                    <TableRow key={accessUser.id}>
                                        <TableCell>
                                            {formatSharedUser(accessUser)}
                                        </TableCell>
                                        <TableCell>
                                            {formatDate(accessUser.access_granted_at)}
                                        </TableCell>
                                        <TableCell>
                                            {accessUser.account_created ? "Yes" : "No"}
                                        </TableCell>
                                        <TableCell align="right">
                                            {!accessUser.is_owner ? (
                                                <Tooltip
                                                    title="Remove access"
                                                    PopperProps={{
                                                        sx: {
                                                            "& .MuiTooltip-tooltip": {
                                                                backgroundColor:
                                                                    "primary.main",
                                                            },
                                                        },
                                                    }}
                                                >
                                                    <IconButton
                                                        onClick={() =>
                                                            setRemoveTarget(accessUser)
                                                        }
                                                    >
                                                        <DeleteOutlineOutlinedIcon color="error" />
                                                    </IconButton>
                                                </Tooltip>
                                            ) : null}
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={4}>
                                        <Typography align="center">
                                            No shared users.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
            <ShareProjectDialog
                open={isShareDialogOpen}
                projectId={projectId}
                accessUsers={accessUsers}
                onClose={() => setIsShareDialogOpen(false)}
                onShared={setAccessUsers}
            />
            <Dialog
                open={Boolean(removeTarget)}
                onClose={() => setRemoveTarget(null)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>Remove access?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ mb: 1 }}>
                        This person will no longer be able to access this project.
                    </Typography>
                    <Typography>
                        Their previously created test cases, tasks, and test runs
                        will remain in the project history.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        color="secondary"
                        onClick={() => setRemoveTarget(null)}
                    >
                        Cancel
                    </Button>
                    <LoadingButton
                        loading={isRemoving}
                        variant="contained"
                        color="error"
                        onClick={handleRemoveAccess}
                    >
                        Remove access
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ShareProjectTab;
